import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

/**
 * useContractSectionCards
 *
 * Drives the timeline + cards experience:
 * - Keeps a local active index in sync with external props (prevents stale props during gestures)
 * - Measures card positions and computes a virtual vertical scroll (translateY) to center the active card
 * - Coalesces wheel/touch so one gesture = exactly one step (+1 / -1)
 * - Exposes helpers/derived values for a dumb UI component
 *
 * Note on touch:
 *  - We DO NOT preventDefault on touchstart/end to keep taps clickable.
 *  - We only preventDefault on touchmove AFTER we decide it's a swipe.
 */
const useContractSectionCards = ({
  sections,
  sectionStatus,
  activeSectionIndex = 0,
  onActiveSectionChange,
  containerRef,
  listRef,
  itemRefs,
  wrapperRef, // element to attach non-passive listeners
}) => {
  // ---------- active index ----------
  const [localIndex, setLocalIndex] = useState(activeSectionIndex)
  useEffect(() => {
    setLocalIndex((prev) =>
      prev !== activeSectionIndex ? activeSectionIndex : prev
    )
  }, [activeSectionIndex])

  const clamp = (n) => Math.max(0, Math.min(n, (sections?.length || 1) - 1))
  const isActive = useCallback(
    (i) => i === clamp(localIndex),
    [localIndex, sections?.length]
  )

  // ---------- measurements ----------
  const [containerH, setContainerH] = useState(0)
  const [centers, setCenters] = useState([]) // offsetTop + h/2 in listRef coords
  const [translateY, setTranslateY] = useState(0)

  const measureAll = useCallback(() => {
    const c = containerRef.current
    if (!c) return
    setContainerH(c.clientHeight)

    const arr = itemRefs.current.map((node) => {
      if (!node) return 0
      const h = node.getBoundingClientRect().height
      return node.offsetTop + h / 2
    })
    setCenters(arr)
  }, [containerRef, itemRefs])

  useLayoutEffect(() => {
    measureAll()
    const ro = new ResizeObserver(measureAll)
    if (containerRef.current) ro.observe(containerRef.current)
    if (listRef.current) ro.observe(listRef.current)
    itemRefs.current.forEach((n) => n && ro.observe(n))
    return () => ro.disconnect()
  }, [measureAll, sections?.length, containerRef, listRef, itemRefs])

  const centerTo = useCallback(
    (idx) => {
      const ci = clamp(idx)
      if (!containerH || centers.length === 0) return
      const target = Math.max(0, (centers[ci] || 0) - containerH / 2)
      setTranslateY(target)
    },
    [containerH, centers]
  )

  useEffect(() => {
    centerTo(localIndex)
  }, [localIndex, centerTo])
  useEffect(() => {
    centerTo(localIndex)
  }, [containerH, centers])

  // ---------- stepping ----------
  const goTo = useCallback(
    (nextIdx) => {
      const next = clamp(nextIdx)
      if (next === localIndex) return
      setLocalIndex(next)
      onActiveSectionChange?.(next)
      centerTo(next)
    },
    [localIndex, onActiveSectionChange, centerTo]
  )

  const stepTo = useCallback(
    (dir) => {
      if (!sections?.length) return
      goTo(localIndex + dir)
    },
    [localIndex, goTo, sections?.length]
  )

  // ---------- wheel (1 burst = 1 step) ----------
  const BURST_MS = 35
  const WHEEL_DEADZONE = 4
  const burstTimerRef = useRef(null)
  const burstActiveRef = useRef(false)

  const handleWheel = useCallback(
    (e) => {
      if (e.ctrlKey) return
      const dy = e.deltaY
      if (Math.abs(dy) <= WHEEL_DEADZONE) return

      // we must cancel default scroll
      e.preventDefault()
      e.stopPropagation()

      if (!burstActiveRef.current) {
        burstActiveRef.current = true
        stepTo(dy > 0 ? 1 : -1)
        clearTimeout(burstTimerRef.current)
        burstTimerRef.current = setTimeout(() => {
          burstActiveRef.current = false
        }, BURST_MS)
        return
      }
      clearTimeout(burstTimerRef.current)
      burstTimerRef.current = setTimeout(() => {
        burstActiveRef.current = false
      }, BURST_MS)
    },
    [stepTo]
  )

  useEffect(() => () => clearTimeout(burstTimerRef.current), [])

  // ---------- touch (1 swipe = 1 step) ----------
  const touchStartY = useRef(0)
  const swipeArmedRef = useRef(false) // becomes true once threshold exceeded
  const swipeFiredRef = useRef(false) // ensure one step per gesture
  const TOUCH_THRESHOLD = 20

  const handleTouchStart = useCallback((e) => {
    // DO NOT preventDefault here -> taps remain clickable
    touchStartY.current = e.touches[0].clientY
    swipeArmedRef.current = false
    swipeFiredRef.current = false
  }, [])

  const handleTouchMove = useCallback(
    (e) => {
      const dy = e.touches[0].clientY - touchStartY.current
      const ady = Math.abs(dy)

      // arm swipe once threshold passed; cancel native scroll only from this moment
      if (ady >= TOUCH_THRESHOLD) {
        swipeArmedRef.current = true
        e.preventDefault() // now we take control of the gesture
        if (!swipeFiredRef.current) {
          swipeFiredRef.current = true
          stepTo(dy < 0 ? 1 : -1)
        }
      }
    },
    [stepTo]
  )

  const handleTouchEnd = useCallback(() => {
    // DO NOT preventDefault here -> taps still produce click
    swipeArmedRef.current = false
    swipeFiredRef.current = false
  }, [])

  // bind non-passive listeners to avoid "Unable to preventDefault" warnings
  useEffect(() => {
    const node = wrapperRef?.current
    if (!node) return
    const opts = { passive: false }

    node.addEventListener("wheel", handleWheel, opts)
    node.addEventListener("touchstart", handleTouchStart, opts)
    node.addEventListener("touchmove", handleTouchMove, opts)
    node.addEventListener("touchend", handleTouchEnd, opts)

    return () => {
      node.removeEventListener("wheel", handleWheel, opts)
      node.removeEventListener("touchstart", handleTouchStart, opts)
      node.removeEventListener("touchmove", handleTouchMove, opts)
      node.removeEventListener("touchend", handleTouchEnd, opts)
    }
  }, [
    wrapperRef,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ])

  // ---------- derived for UI ----------
  const handleCardClick = useCallback((i) => goTo(i), [goTo])

  const answeredCount = Object.values(sectionStatus || {}).filter(
    (v) => v === "understood" || v === "notUnderstood"
  ).length

  const spacerH = Math.max(0, Math.floor((containerH || 0) / 2))

  const getDotColor = useCallback(
    (section, index) => {
      const understood = sectionStatus[index] === "understood"
      const unclear = sectionStatus[index] === "notUnderstood"
      if (understood) return "bg-success-default"
      if (unclear || section?.risky) return "bg-danger-default"
      return "bg-warning-default"
    },
    [sectionStatus]
  )

  return {
    localIndex,
    isActive,
    translateY,
    centers,
    containerH,
    spacerH,
    answeredCount,
    handleCardClick,
    goTo,
    stepTo,
    getDotColor,
  }
}

export default useContractSectionCards
